import { theme, Tooltip } from 'antd'

import { formatAmount } from '@/utils/count'

export interface UsageSegment {
  label: string
  value: number
}

function useUsageColors() {
  const { token } = theme.useToken()
  return {
    Sold: token.colorPrimary,
    'Internal groups': token.colorWarning,
    'Platform pods': token.colorTextTertiary,
    Platform: token.colorTextTertiary,
    Other: token.colorBorder,
    Free: token.colorSuccessBorder,
    Empty: token.colorFillSecondary,
  } as Record<string, string>
}

/** Segments are shares of their sum; measured free and recorded use need not add up to the total. */
export function UsageBar({
  segments,
  label,
}: {
  segments: UsageSegment[]
  label: string
}) {
  const colors = useUsageColors()
  const shown = segments.filter((segment) => segment.value > 0)
  const sum = shown.reduce((total, segment) => total + segment.value, 0)
  const description = shown
    .map((segment) => `${segment.label}: ${formatAmount(segment.value)}`)
    .join(', ')
  return (
    <Tooltip
      title={
        <>
          {shown.map((segment) => (
            <div key={segment.label}>
              {segment.label}: {formatAmount(segment.value)} (
              {((segment.value / sum) * 100).toFixed(1)}%)
            </div>
          ))}
        </>
      }
    >
      <div
        role="img"
        tabIndex={0}
        aria-label={`${label}. ${description}`}
        style={{
          display: 'flex',
          height: 10,
          minWidth: 120,
          borderRadius: 5,
          overflow: 'hidden',
          background: colors.Empty,
        }}
      >
        {sum > 0 &&
          shown.map((segment) => (
            <div
              key={segment.label}
              style={{
                width: `${(segment.value / sum) * 100}%`,
                background: colors[segment.label],
              }}
            />
          ))}
      </div>
    </Tooltip>
  )
}

export function UsageLegend() {
  const colors = useUsageColors()
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {['Sold', 'Internal groups', 'Platform pods', 'Other', 'Free'].map(
        (label) => (
          <span key={label} style={{ display: 'inline-flex', gap: 6 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                marginTop: 5,
                background: colors[label],
              }}
            />
            {label}
          </span>
        ),
      )}
    </div>
  )
}
